import { ArrowLeft, Home, User, Menu, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { PWAStatus } from "@/components/PWAInstallBanner"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet"
import { useNavigate, useLocation, Link } from "react-router-dom"
import { useAuth } from "@/hooks/useAuth"
import { useIsMobile } from "@/hooks/use-mobile"
import { useState } from "react"

const NavigationBar = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const isMobile = useIsMobile()
  const [isOpen, setIsOpen] = useState(false)

  // Don't show navigation bar on homepage
  if (location.pathname === '/') {
    return null
  }

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1)
    } else {
      navigate('/')
    }
  }

  const navigationItems = [
    { path: "/", label: "Startseite", icon: Home },
    { path: "/listings", label: "Unterkünfte", icon: null },
    { path: "/help", label: "Hilfe", icon: null },
    ...(user ? [
      { path: "/bookings", label: "Meine Buchungen", icon: null },
      { path: "/profile", label: "Profil", icon: User },
    ] : [])
  ]

  if (isMobile) {
    return (
      <div className="sticky top-16 sm:top-18 z-40 w-full border-b bg-background/95 backdrop-blur-sm">
        <div className="container mx-auto px-4">
          <div className="flex h-14 sm:h-16 items-center justify-between">
            {/* Back + Home buttons */}
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="default" 
                onClick={handleBack}
                className="gap-2 min-h-[48px] px-3"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="text-sm">Zurück</span>
              </Button>
              <Link to="/">
                <Button
                  variant="ghost"
                  size="default"
                  className="min-h-[48px] px-3"
                  title="Startseite"
                >
                  <Home className="h-4 w-4" />
                </Button>
              </Link>
            </div>

            <span className="text-sm font-medium text-muted-foreground truncate max-w-[120px]">
              {getPageTitle(location.pathname)}
            </span>

            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="default" data-tour="user-menu" className="min-h-[48px] min-w-[48px]">
                  <Menu className="h-4 w-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72">
                <SheetHeader>
                  <SheetTitle>Navigation</SheetTitle>
                </SheetHeader>
                <div className="mt-6 space-y-2">
                  {navigationItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setIsOpen(false)}
                      className="flex items-center gap-3 p-4 rounded-lg hover:bg-muted transition-colors min-h-[48px]"
                    >
                      {item.icon && <item.icon className="h-4 w-4" />}
                      <span className="text-base">{item.label}</span>
                    </Link>
                  ))}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="sticky top-14 sm:top-16 z-40 w-full border-b bg-background/95 backdrop-blur-sm">
      <div className="container mx-auto px-4">
        <div className="flex h-11 sm:h-12 items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBack}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Zurück</span>
          </Button>

          <div className="h-4 w-px bg-border" />

          <Link to="/">
            <Button
              variant="ghost"
              size="sm"
              className="gap-2"
            >
              <Home className="h-4 w-4" />
              <span className="hidden sm:inline">Startseite</span>
            </Button>
          </Link>

          <div className="ml-auto flex items-center gap-2" data-tour="user-menu">
            <PWAStatus />
            <span className="text-sm text-muted-foreground">
              {getPageTitle(location.pathname)}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

const getPageTitle = (pathname: string): string => {
  const routes: Record<string, string> = {
    '/auth': 'Anmeldung',
    '/listings': 'Unterkünfte',
    '/create-listing': 'Unterkunft erstellen',
    '/my-listings': 'Meine Objekte',
    '/bookings': 'Buchungen',
    '/profile': 'Profil',
    '/host-dashboard': 'Host Dashboard',
    '/messages': 'Nachrichten',
    '/ai-travel': 'KI-Reiseplaner',
    '/help': 'Hilfe & Support',
    '/contact': 'Kontakt',
    '/admin': 'Admin Panel',
    '/datenschutz': 'Datenschutz',
    '/agb': 'AGB',
    '/impressum': 'Impressum',
    '/tax-profile': 'Steuer Profil'
  }

  if (pathname.startsWith('/listing/')) {
    return 'Unterkunft Details'
  }
  if (pathname.startsWith('/ai-pricing/')) {
    return 'KI-Preisoptimierung'
  }

  return routes[pathname] || 'Seite'
}

export default NavigationBar